import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import api from '../sever/Api';

const AdminUserManagement = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('view');
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pageCount: 0, hasNext: false, hasPrev: false, nextPage: null, prevPage: null });
  const [roleFilter, setRoleFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '', isActive: true, role: 'user' });
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState('');

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin') {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  const fetchUsers = async (targetPage = page) => {
    setLoading(true);
    setError('');
    try {
      const roleParam = roleFilter === 'all' ? undefined : roleFilter;
      const res = await api.get('/api/admin/users', { params: { page: targetPage, limit, role: roleParam } });
      setUsers(res.data?.users || []);
      if (res.data?.pagination) {
        setPagination(res.data.pagination);
        setPage(res.data.pagination.page || targetPage);
      } else {
        setPagination({ total: (res.data?.users || []).length, page: targetPage, limit, pageCount: 1, hasNext: false, hasPrev: targetPage > 1, nextPage: null, prevPage: targetPage > 1 ? targetPage - 1 : null });
      }
    } catch (e) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(1);
  }, [roleFilter]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const resetForm = () => setForm({ firstName: '', lastName: '', email: '', password: '', isActive: true, role: 'user' });

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await api.post('/api/admin/users', form);
      resetForm();
      setActiveTab('view');
      fetchUsers();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to add user');
    } finally {
      setLoading(false);
    }
  };

  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(val || '').trim());

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedUserId) return;

    setLoading(true);
    setError('');

    try {
      const safeTrim = (v) => (typeof v === 'string' ? v.trim() : v);

      const payload = {
        firstName: safeTrim(form.firstName),
        lastName: safeTrim(form.lastName),
        isActive: !!form.isActive,
        role: form.role === 'admin' ? 'admin' : 'user',
      };

      const currentEmail = selectedUser?.email || '';
      const newEmail = safeTrim(form.email);

      if (newEmail && newEmail !== currentEmail) {
        if (!isValidEmail(newEmail)) {
          setError('Please enter a valid email address');
          setLoading(false);
          return;
        }
        payload.email = newEmail;
      }

      // API call with payload
      await api.put(`/api/admin/users/${selectedUserId}`, payload);

      // After success
      setActiveTab('view');
      resetForm();  
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };


  const handleDelete = (id) => {
    setConfirmDeleteId(id);
  };

  const confirmDelete = async () => {
    if (!confirmDeleteId) return;
    setLoading(true);
    setError('');
    try {
      await api.delete(`/api/admin/users/${confirmDeleteId}`);
      setConfirmDeleteId('');
      fetchUsers();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const beginEdit = (u) => {
    setSelectedUserId(u._id);
    setSelectedUser(u);
    setForm({
      firstName: u.firstName || '',
      lastName: u.lastName || '',
      email: u.email || '',
      password: '',
      isActive: u.isActive !== false,
      role: (u.role === 'admin' ? 'admin' : 'user'),
    });
    setActiveTab('edit');
  };

  const filteredUsers = useMemo(() => {
    const roleFiltered = (() => {
      const selected = String(roleFilter || 'all').toLowerCase();
      if (selected === 'all') return users;
      return users.filter((u) => String(u.role || '').toLowerCase() === selected);
    })();
    if (planFilter === 'all') return roleFiltered;

    const normalizePlan = (u) => {
      const raw = String(
        u.subscriptionPlan ||
        u.plan ||
        (u.role === 'premium' ? 'premium' : 'free')
      ).toLowerCase();

      if (raw === 'premium') return 'premium';
      if (raw === 'professional') return 'professional';
      if (raw === 'standard') return 'standard';
      if (raw === 'free') return 'free';
      // legacy fallback
      if (u.role === 'premium') return 'premium';
      return 'free';
    };

    return roleFiltered.filter((u) => normalizePlan(u) === planFilter);
  }, [users, roleFilter, planFilter]);

  const visiblePages = useMemo(() => {
    const count = pagination.pageCount || 0;
    if (count <= 1) return [1].slice(0, count);
    const current = page;
    let start = Math.max(1, current - 2);
    let end = Math.min(count, start + 4);
    start = Math.max(1, end - 4);
    const pages = [];
    for (let p = start; p <= end; p += 1) pages.push(p);
    return pages;
  }, [pagination.pageCount, page]);

  const renderView = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold">All Users</h2>
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="px-2 py-1 border rounded text-sm">
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
          </select>
          <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} className="px-2 py-1 border rounded text-sm">
            <option value="all">All Plans</option>
            <option value="free">Free</option>
            <option value="standard">Standard</option>
            <option value="professional">Professional</option>
            <option value="premium">Premium</option>
          </select>
        </div>
        <button style={{background:'rgb(179, 157, 219)'}} onClick={() => { resetForm(); setActiveTab('add'); }} className="px-4 py-2 bg-blue-600 text-white rounded">Add User</button>
      </div>
      {loading ? (
        <div>Loading...</div>
      ) : error ? (
        <div className="text-red-600 text-sm">{error}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border">
            <thead>
              <tr className="bg-gray-50 text-left text-sm">
                <th className="px-3 py-2 border">Name</th>
                <th className="px-3 py-2 border">Email</th>
                <th className="px-3 py-2 border">Role</th>
                <th className="px-3 py-2 border">Subscription</th>
                <th className="px-3 py-2 border">Status</th>
                <th className="px-3 py-2 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u._id} className="text-sm">
                  <td className="px-3 py-2 border">{[u.firstName, u.lastName].filter(Boolean).join(' ') || u.name || '-'}</td>
                  <td className="px-3 py-2 border">{u.email}</td>
                  <td className="px-3 py-2 border">{u.role}</td>
                  <td className="px-3 py-2 border">{(() => {
                    const raw = String(u.subscriptionPlan || u.plan || (u.role === 'premium' ? 'premium' : 'free')).toLowerCase();
                    if (raw === 'premium') return 'Premium';
                    if (raw === 'professional') return 'Professional';
                    if (raw === 'standard') return 'Standard';
                    return 'Free';
                  })()}</td>
                  <td className="px-3 py-2 border">{u.isActive ? 'Active' : 'Inactive'}</td>
                  <td className="px-3 py-2 border space-x-2">
                    <button onClick={() => beginEdit(u)} className="px-3 py-1 text-white bg-emerald-600 rounded">Edit</button>
                    <button onClick={() => handleDelete(u._id)} className="px-3 py-1 text-white bg-red-600 rounded">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-600">
              Showing page {pagination.page} of {Math.max(pagination.pageCount || 0, 1)}
            </div>
            <div className="flex items-center gap-1">
              <button
                className={`px-3 py-1 border rounded ${pagination.hasPrev ? 'bg-white hover:bg-gray-50' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                disabled={!pagination.hasPrev || loading}
                onClick={() => fetchUsers(page - 1)}
              >
                Prev
              </button>
              {visiblePages.map((p) => (
                <button
                  key={p}
                  className={`px-3 py-1 border rounded ${p === page ? 'bg-blue-600 text-white border-blue-600' : 'bg-white hover:bg-gray-50'}`}
                  disabled={loading}
                  onClick={() => fetchUsers(p)}
                >
                  {p}
                </button>
              ))}
              <button
                className={`px-3 py-1 border rounded ${pagination.hasNext ? 'bg-white hover:bg-gray-50' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                disabled={!pagination.hasNext || loading}
                onClick={() => fetchUsers(page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderAddOrEdit = (mode) => (
    <form onSubmit={mode === 'add' ? handleCreate : handleUpdate} className="space-y-4 max-w-xl">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm text-gray-700 mb-1">First Name</label>
          <input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className="w-full px-3 py-2 border rounded" required />
        </div>
        <div>
          <label className="block text-sm text-gray-700 mb-1">Last Name</label>
          <input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className="w-full px-3 py-2 border rounded" required />
        </div>
      </div>
      <div>
        <label className="block text-sm text-gray-700 mb-1">Email</label>
        <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border rounded" required />
      </div>
      {mode === 'add' && (
        <div>
          <label className="block text-sm text-gray-700 mb-1">Password</label>
          <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full px-3 py-2 border rounded" required />
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm text-gray-700 mb-1">Status</label>
          <select value={form.isActive ? 'active' : 'inactive'} onChange={(e) => setForm({ ...form, isActive: e.target.value === 'active' })} className="w-full px-3 py-2 border rounded">
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-700 mb-1">Role</label>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 border rounded">
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </div>
      </div>
      <div className="flex gap-2">
        <button style={{background:'rgb(179, 157, 219)'}} type="submit" disabled={loading} className="px-5 py-2 bg-blue-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2">
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>{mode === 'add' ? 'Adding...' : 'Updating...'}</span>
            </>
          ) : (
            <span>{mode === 'add' ? 'Add User' : 'Save Changes'}</span>
          )}
        </button>
        <button
          type="button"
          onClick={() => {
            resetForm();
            setActiveTab('view');
            setRoleFilter('all');
            fetchUsers();
          }}
          disabled={loading}
          className="px-5 py-2 bg-gray-200 text-gray-800 rounded"
        >
          Cancel
        </button>

      </div>
    </form>
  );

  return (
    <div className='flex flex-col lg:flex-row min-h-screen w-full overflow-x-hidden'>
      <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
        <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      </div>
      <div className='ml-64 flex-1 relative'>
        <Navbar toggleSidebar={toggleSidebar} />
        <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-[#EFF6FF] via-white to-[#F9FAFB]">
          <div className="w-full max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-800">User Management</h1>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="flex border-b">
                <button className={`px-6 py-3 text-sm ${activeTab === 'view' ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`} onClick={() => setActiveTab('view')}>View All Users</button>
                <button className={`px-6 py-3 text-sm ${activeTab === 'add' ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`} onClick={() => { resetForm(); setActiveTab('add'); }}>Add User</button>
                {activeTab === 'edit' && <button className={`px-6 py-3 text-sm border-b-2 border-blue-600 text-blue-600 bg-blue-50`} disabled>Edit User</button>}
              </div>
              <div className="p-6">
                {activeTab === 'view' && renderView()}
                {activeTab === 'add' && renderAddOrEdit('add')}
                {activeTab === 'edit' && renderAddOrEdit('edit')}
              </div>
            </div>
          </div>
        </div>
        {Boolean(confirmDeleteId) && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center">
            <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setConfirmDeleteId('')} />
            <div className="relative bg-white rounded-lg shadow-lg w-80 p-5 z-[61]">
              <h3 className="text-base font-semibold mb-2">Are you sure you want to delete this user?</h3>
              <p className="text-sm text-gray-600 mb-4">This action cannot be undone.</p>
              <div className="flex justify-end gap-2">
                <button className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50" onClick={() => setConfirmDeleteId('')}>No</button>
                <button className="px-4 py-2 text-sm rounded text-white bg-red-500 hover:bg-red-600" onClick={confirmDelete} disabled={loading}>{loading ? 'Deleting...' : 'Yes, Delete'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUserManagement;


